/**
 * Turning a click into a world intent.
 *
 * Nothing in this scene is pickable in Babylon's sense — the terrain mesh is a
 * clipmap of grid indices whose real shape only exists in the vertex shader,
 * and every mesh sets `isPickable = false`. So the ground is found by marching
 * the CPU heightfield with the same `groundRay` the spells aim with, and actors
 * are tested as upright capsules against the same ray.
 */

import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { groundRay } from "../spells/bending.js";

const _origin = new Vector3();
const _dir = new Vector3();

/** How far a click can reach across the snowfield. */
const PICK_RANGE = 260;

/**
 * Build the world-space ray through a client-space pixel.
 *
 * Written by hand rather than with `scene.createPickingRay` because the post
 * chain freezes a TAA-jittered projection onto the camera every frame
 * (`post/postChain.js`), and the rig already publishes a clean basis.
 *
 * @param {import("../core/camera.js").CameraRig} rig
 * @param {HTMLCanvasElement} canvas
 * @param {number} clientX
 * @param {number} clientY
 * @returns {{origin: Vector3, dir: Vector3}}
 */
export function screenRay(rig, canvas, clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = 1 - ((clientY - rect.top) / rect.height) * 2;

    const aspect = rect.width / Math.max(1, rect.height);
    const tanHalf = Math.tan(rig.fov * 0.5); // Babylon's fov is vertical

    const f = rig.forward;
    const r = rig.right;
    // The rig's `up` is the cross of right×forward, which in Babylon's
    // left-handed basis points *down*. Flip it rather than re-deriving.
    const ux = -rig.up.x;
    const uy = -rig.up.y;
    const uz = -rig.up.z;

    const sx = ndcX * tanHalf * aspect;
    const sy = ndcY * tanHalf;

    _dir.set(
        f.x + r.x * sx + ux * sy,
        f.y + r.y * sx + uy * sy,
        f.z + r.z * sx + uz * sy
    );
    _dir.normalize();
    _origin.copyFrom(rig.camera.position);

    return { origin: _origin, dir: _dir };
}

/**
 * Where a ray meets the snow.
 *
 * @param {{heightAt(x:number,z:number):number}} terrain
 * @returns {{x:number, y:number, z:number}|null}
 */
export function groundPick(terrain, origin, dir) {
    const t = groundRay(
        terrain,
        origin.x, origin.y, origin.z,
        dir.x, dir.y, dir.z,
        PICK_RANGE
    );
    if (t < 0) return null;
    return {
        x: origin.x + dir.x * t,
        y: origin.y + dir.y * t,
        z: origin.z + dir.z * t,
    };
}

/**
 * Nearest actor whose capsule the ray enters.
 *
 * Actors are upright capsules: a vertical segment from the feet up, with a
 * radius. Cheap enough to brute force — there are a dozen of them, not a
 * thousand, and a spatial index for twelve boxes is a lie you tell yourself.
 *
 * @param {Array<{position: Vector3, pickRadius: number, pickHeight: number, isDead?: boolean}>} actors
 * @returns {{actor: any, dist: number}|null}
 */
export function actorPick(actors, origin, dir) {
    let best = null;
    let bestT = Infinity;

    for (const a of actors) {
        if (!a || a.isDead) continue;
        const t = raySegmentCapsule(
            origin, dir,
            a.position.x, a.position.y, a.position.z,
            a.pickHeight ?? 1.9,
            a.pickRadius ?? 0.55
        );
        if (t >= 0 && t < bestT) {
            bestT = t;
            best = a;
        }
    }
    return best ? { actor: best, dist: bestT } : null;
}

/**
 * Ray against a vertical capsule (feet at `by`, extending `height` up).
 * Returns the entry distance, or -1.
 */
function raySegmentCapsule(origin, dir, bx, by, bz, height, radius) {
    // Infinite-cylinder test in XZ, then clamp against the vertical extent and
    // fall back to sphere caps. Standard, and short enough to keep inline.
    const ox = origin.x - bx;
    const oz = origin.z - bz;

    const a = dir.x * dir.x + dir.z * dir.z;
    const b = 2 * (ox * dir.x + oz * dir.z);
    const c = ox * ox + oz * oz - radius * radius;

    let t = -1;
    if (a > 1e-6) {
        const disc = b * b - 4 * a * c;
        if (disc >= 0) {
            const sq = Math.sqrt(disc);
            const t0 = (-b - sq) / (2 * a);
            const t1 = (-b + sq) / (2 * a);
            for (const cand of [t0, t1]) {
                if (cand < 0) continue;
                const y = origin.y + dir.y * cand;
                if (y >= by && y <= by + height) {
                    t = cand;
                    break;
                }
            }
        }
    }
    if (t >= 0) return t;

    // Caps: two spheres, one at each end of the segment.
    const capA = raySphere(origin, dir, bx, by + radius, bz, radius);
    const capB = raySphere(origin, dir, bx, by + height - radius, bz, radius);
    if (capA >= 0 && capB >= 0) return Math.min(capA, capB);
    if (capA >= 0) return capA;
    return capB;
}

function raySphere(origin, dir, cx, cy, cz, radius) {
    const ox = origin.x - cx;
    const oy = origin.y - cy;
    const oz = origin.z - cz;
    const b = 2 * (ox * dir.x + oy * dir.y + oz * dir.z);
    const c = ox * ox + oy * oy + oz * oz - radius * radius;
    const disc = b * b - 4 * c;
    if (disc < 0) return -1;
    const sq = Math.sqrt(disc);
    const t0 = (-b - sq) * 0.5;
    if (t0 >= 0) return t0;
    const t1 = (-b + sq) * 0.5;
    return t1 >= 0 ? t1 : -1;
}
